import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Typography,
  Row,
  Col,
  Statistic,
  Button,
  Space,
  message,
} from 'antd';
import {
  FileTextOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  BarChartOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import InvoiceList from './components/InvoiceList';
import RevenueReportDashboard from './components/RevenueReportDashboard';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface InvoiceStatistics {
  general_statistics: {
    total_invoices: number;
    paid_invoices: number;
    overdue_invoices: number;
    unpaid_invoices: number;
    total_billed: number;
    total_paid: number;
    total_outstanding: number;
    average_invoice_amount: number;
  };
  status_distribution: Array<{
    status: string;
    count: number;
    total_amount: number;
  }>;
  payment_methods: Array<{
    payment_method: string;
    count: number;
    total_paid: number;
  }>;
}

const GestionFactures: React.FC = () => {
  const [statistics, setStatistics] = useState<InvoiceStatistics | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  const { hasPermission } = useAuth();

  // Fonction utilitaire pour formater les montants avec séparateurs de milliers
  const formatAmount = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined || isNaN(amount)) return '0';
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.round(amount)); // Arrondir pour éviter les décimales
  };

  // Fonction pour formater les montants avec la devise
  const formatCurrency = (amount: number | null | undefined): string => {
    const formatted = formatAmount(amount);
    return `${formatted} XOF`;
  };

  // Charger les statistiques globales
  const fetchStatistics = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/subscription/invoices/statistics', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 403) {
        message.error('Accès refusé : Vous n\'avez pas les permissions nécessaires');
        return;
      }

      if (response.status === 401) {
        message.error('Session expirée : Veuillez vous reconnecter');
        return;
      }

      if (!response.ok) {
        // Tenter de lire le corps de la réponse d'erreur
        let errorDetails = '';
        try {
          const errorData = await response.json();
          errorDetails = errorData.message || errorData.error || JSON.stringify(errorData);
        } catch (e) {
          errorDetails = response.statusText;
        }

        const errorMessage = `Erreur HTTP ${response.status}: ${errorDetails}`;
        console.error('❌ Erreur API statistics:', errorMessage);
        console.error('📍 URL:', '/api/subscription/invoices/statistics');
        console.error('🔑 Token présent:', !!localStorage.getItem('authToken'));

        message.error(`Erreur ${response.status}: ${errorDetails}`);
        throw new Error(errorMessage);
      }

      const data = await response.json();

      if (data.success) {
        setStatistics(data.data);
        console.log('✅ Statistiques chargées avec succès');
      } else {
        const errorMsg = data.message || 'Erreur lors du chargement des statistiques';
        console.error('❌ Réponse API avec success=false:', data);
        message.error(errorMsg);
      }
    } catch (error: any) {
      console.error('❌ Exception lors du chargement des statistiques:', error);
      console.error('📍 Message:', error.message);
      console.error('📍 Stack:', error.stack);

      // Afficher un message plus précis
      if (error.message && error.message.includes('Erreur HTTP')) {
        // Déjà affiché par le bloc if (!response.ok)
      } else {
        message.error(`Erreur: ${error.message || 'Connexion au serveur impossible'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasPermission('Admin.Souscription.Invoices.view')) {
      fetchStatistics();
    }
  }, [hasPermission]);

  // Vérification des permissions
  if (!hasPermission('Admin.Souscription.Invoices.view')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <FileTextOutlined className="text-4xl text-gray-400 mb-4" />
          <Title level={4} type="secondary">Accès non autorisé</Title>
          <Text type="secondary">
            Vous n'avez pas les permissions nécessaires pour accéder à la gestion des factures.
          </Text>
        </div>
      </div>
    );
  }

  // Calculer le taux de paiement
  const paymentRate = statistics?.general_statistics.total_invoices
    ? Math.round(
        (statistics.general_statistics.paid_invoices /
          statistics.general_statistics.total_invoices) *
          100
      )
    : 0;

  return (
    <div className="p-6">
      {/* En-tête */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <Title level={2}>
              <FileTextOutlined className="mr-2" />
              Gestion des Factures
            </Title>
            <Text type="secondary">
              Gérez et suivez toutes les factures de souscription
            </Text>
          </div>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchStatistics}
            loading={loading}
          >
            Actualiser
          </Button>
        </div>
      </div>

      {/* Statistiques globales */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Factures"
              value={statistics?.general_statistics.total_invoices || 0}
              groupSeparator=" "
              precision={0}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Montant Total Facturé"
              value={statistics?.general_statistics.total_billed || 0}
              formatter={(value) => formatCurrency(value as number)}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Montant Total Payé"
              value={statistics?.general_statistics.total_paid || 0}
              formatter={(value) => formatCurrency(value as number)}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
            <div className="mt-2">
              <Text type="secondary">
                Taux de paiement: <strong>{paymentRate}%</strong>
              </Text>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="En Attente de Paiement"
              value={statistics?.general_statistics.total_outstanding || 0}
              formatter={(value) => formatCurrency(value as number)}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
            <div className="mt-2">
              <Text type="secondary">
                Factures impayées: <strong>{formatAmount(statistics?.general_statistics.unpaid_invoices)}</strong>
              </Text>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Alertes */}
      {statistics && statistics.general_statistics.overdue_invoices > 0 && (
        <Card className="mb-6 border-l-4 border-l-red-500 bg-red-50">
          <div className="flex items-center">
            <WarningOutlined className="text-2xl text-red-500 mr-4" />
            <div>
              <Text strong className="text-red-700">
                {formatAmount(statistics.general_statistics.overdue_invoices)} facture(s) en retard
              </Text>
              <div className="text-sm text-red-600 mt-1">
                Vérifiez les factures en retard et envoyez des rappels de paiement si nécessaire.
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Onglets */}
      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane
            tab={
              <span>
                <FileTextOutlined />
                Toutes les Factures
              </span>
            }
            key="all"
          >
            <InvoiceList />
          </TabPane>

          <TabPane
            tab={
              <span>
                <ClockCircleOutlined />
                En Attente
              </span>
            }
            key="unpaid"
          >
            <InvoiceList />
          </TabPane>

          <TabPane
            tab={
              <span>
                <WarningOutlined />
                En Retard
              </span>
            }
            key="overdue"
          >
            <InvoiceList />
          </TabPane>

          <TabPane
            tab={
              <span>
                <CheckCircleOutlined />
                Payées
              </span>
            }
            key="paid"
          >
            <InvoiceList />
          </TabPane>

          <TabPane
            tab={
              <span>
                <BarChartOutlined />
                Rapports & Chiffre d'Affaires
              </span>
            }
            key="reports"
          >
            <RevenueReportDashboard />
          </TabPane>
        </Tabs>
      </Card>

      {/* Statistiques par méthode de paiement */}
      {statistics && statistics.payment_methods && statistics.payment_methods.length > 0 && (
        <Row gutter={[16, 16]} className="mt-6">
          <Col span={24}>
            <Card title="Répartition par Méthode de Paiement" size="small">
              <Row gutter={[16, 16]}>
                {statistics.payment_methods.map((method, index) => (
                  <Col xs={24} sm={12} md={6} key={index}>
                    <Card size="small" className="bg-gray-50">
                      <Statistic
                        title={method.payment_method || 'Non spécifié'}
                        value={method.total_paid || 0}
                        formatter={(value) => formatCurrency(value as number)}
                        valueStyle={{ fontSize: '16px' }}
                      />
                      <Text type="secondary" className="text-xs">
                        {formatAmount(method.count)} paiement(s)
                      </Text>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default GestionFactures;